import { utils } from "../utils/Utils.jsx";

export const bankDetails = {
  accountNumber: "account-number",
  achAccountType: "account-type",
  accountType: "account-type",
  accountName: "account-name",
  bankName: "bank-name",
  bankAddress: "bank-address",
  beneficiaryAddress: "beneficiary-address",
  beneficiartAddress: "beneficiary-address",
  swiftCode: "swift-code",
  routingNumber: "routing-number",
  depositCode: "deposit-code",
  memo: "memo",
};

export function getPaymentVendorStatuses(key, localizedText) {
  const vendorStatuses = {
    CREATED: localizedText["initial-account-creation"],
    SUBMITTED_TO_TRUST: localizedText["initial-trust-account-creation"],
    PRE_OPEN_AT_TRUST: localizedText["created-at-trust-but-pending-items"],
    UPLOADED_DOCUMENTS: localizedText["documents-uploaded"],
    KYC_REQUIRED_ACTION: localizedText["pending-kyc-action"],
    OPEN: localizedText["open"],
    CLOSED: localizedText["account-closed"],
    BLOCKED: localizedText["account-blocked"],
    BLACKLISTED: localizedText["account-blacklisted"],
    PENDING_TRUST_ACCOUNT:
      localizedText["attempted-to-open-trust-account-but-failed"],
    PENDING_CLIENT_APPROVAL: localizedText["pending-client-admin-approval"],
    PENDING_BLOX_APPROVAL: localizedText["pending-blox-approval"],
  };

  // Return the status based on the key or fallback to the description
  return vendorStatuses[key] ?? key;
}

export function getPaymentMethodTypes(key, description, localizedText) {
  if (key == "venmo" || key == "paypal") {
    return utils.capitalizeFirstLetter(key);
  } else {
    const paymentAccountTypes = {
      bank_wire_intl: localizedText["wire-international"],
      crypto: localizedText["crypto-currency"],
      card: localizedText["credit-debit-card"],
      cheque: localizedText["physical-check"],
      ach: localizedText["ach-transfer"],
      bank_wire_us: localizedText["wire-us"],
      checkbook: localizedText["checkbook"],
      rtp: localizedText["rtp-transfer"],
    };

    return paymentAccountTypes[key] ?? description;
  }
}

export function getPaymentMethodLabels(key, description, localizedText) {
  const fieldMapping = {
    acount_name: localizedText["name-on-the-account"],
    country: localizedText["country-of-bank-account"],
    recipient_name:
      localizedText["name-cheque-will-me-written-to.-<=-40-characters"],
    address_1: localizedText["address-line"] + " 1",
    address_2: localizedText["address-line"] + " 2",
    city: localizedText["city"],
    state: localizedText["state-2-letter-abbreviation-province-region"],
    postal_code: localizedText["postal-zip-code"],
    phone_number: localizedText["phone-number"],
    routing_number: localizedText["routing-number"],
    account_type: localizedText["checking-or-saving"],
    account_class: localizedText["business-or-personal"],
    account_number: localizedText["bank-account-number"],
    swift_bic_code: localizedText["swift-/-bic-code"],
    venmo_user_name: localizedText["venmo-@username"],
    phone: localizedText["phone-registered-at-venmo"],
    email_address: localizedText["email-address"],
    paypal_user_name: localizedText["paypal-username"],
    token_symbol: localizedText["asset-network"],
    wallet_address: localizedText["wallet-address"],
    wallet_label: localizedText["wallet-internal-name"],
    name_on_card: localizedText["name-of-bank-account"],
    beneficiary_name: localizedText["beneficiary-name"],
    bank_name: localizedText["bank-name"],
  };

  return fieldMapping[key] ?? description;
}

export function getPaymentMethodValues(key, localizedText) {
  const fieldMapping = {
    CHECKING: localizedText["checking"],
    SAVING: localizedText["savings"],
    BUSINESS: localizedText["business"],
    PERSONAL: localizedText["personal"],
  };

  return fieldMapping[key] ?? key;
}

export function getTransactionType(key, description, localizedText) {
  const types = {
    DEBIT_ADJUSTMENT: localizedText["debit-adjustment"],
    CREDIT_ADJUSTMENT: localizedText["credit-adjustment"],
    WITHDRAW: localizedText["withdraw-crypto"],
    DEPOSIT: localizedText["deposit-into-account"],
    HOLD: localizedText["temporary-hold"],
    RELEASE: localizedText["release"],
    BUY: localizedText["buy-an-instrument"],
    SELL: localizedText["sell-an-instrument"],
    INTRA_ACCOUNT_TRANSFER_WITHDRAW:
      localizedText["intra-account-transfer-withdraw"],
    INTRA_ACCOUNT_TRANSFER_DEPOSIT:
      localizedText["intra-account-transfer-deposit"],
    CONVERSION_WITHDRAW: localizedText["conversion-withdraw"],
    CONVERSION_DEPOSIT: localizedText["conversion-deposit"],
    OTC_TRADE: localizedText["otc-trade"],
    CONVERSION_DEPOSIT_HOLD: localizedText["conversion-deposit-hold"],
    PAYME_DEPOSIT_HOLD: localizedText["payme-deposit-hold"],
  };

  return types[key] ?? description;
}

export function getTransactionSubType(key, description, localizedText) {
  const subtypes = {
    SEND_TO_PEER: localizedText["send-to-peer"],
    RECEIVED_FROM_PEER: localizedText["received-from-peer"],
    ACCOUNT_ADJUSTMENT: localizedText["account-adjustment"],
    NONE: localizedText["none"],
    PAY_VENDOR: localizedText["send-a-vendor-payment"],
    CONVERSION: localizedText["conversion"],
    PAYMENT_FEE: localizedText["payment-fee"],
    PAY_ME: localizedText["payment-processor"],
    PAYMENT_BUTTON: localizedText["payment-button"],
    CREDIT_CARD_SERVICE_FEE: localizedText["credit-card-service-fee"],
    STRIPE_CREDIT_CARD_FEE: localizedText["stripe-credit-card-fee"],
    STRIPE_INTERNATIONAL_CREDIT_CARD_FEE:
      localizedText["stripe-international-credit-card-fee"],
    BLOXCROSS_CREDIT_CARD_PERCENTAGE_FEE:
      localizedText["bloxcross-credit-card-percentage-fee"],
    STRIPE_CREDIT_CARD_US_PERCENTAGE_FEE:
      localizedText["stripe-credit-card-us-percentage-fee"],
    STRIPE_CREDIT_CARD_INTERNATIONAL_PERCENTAGE_FEE:
      localizedText["stripe-credit-card-international-percentage-fee"],
    STRIPE_CREDIT_CARD_FIXED_FEE: localizedText["stripe-credit-card-fixed-fee"],
    BLOXCROSS_SERVICE_CREDIT_CARD_FEE:
      localizedText["bloxcross-service-credit-card-fee"],
    STRIPE_SERVICE_CREDIT_CARD_FEE:
      localizedText["stripe-service-credit-card-fee"],
    ELAVON_SERVICE_CREDIT_CARD_FEE:
      localizedText["elavon-service-credit-card-fee"],
    PAYMENT_FIXED_FEE: localizedText["payment-fixed-fee"],
    BLOX_FEE: localizedText["blox-fee"],
    WITHDRAW: localizedText["withdraw"],
    NETWORK_FEE: localizedText["network-fee"],
  };

  return subtypes[key] ?? description;
}

export function getFeeType(key, description, localizedText) {
  const types = {
    amount: localizedText["amount"],
    source_fee: localizedText["source-fee"],
    pay_fee: localizedText["pay-fee"],
  };

  return types[key] ?? description;
}

export function getMonths(localizedText, monthKey) {
  var monthName = "";

  if (monthKey === "01") {
    monthName = localizedText["january"];
  } else if (monthKey === "02") {
    monthName = localizedText["february"];
  } else if (monthKey === "03") {
    monthName = localizedText["march"];
  } else if (monthKey === "04") {
    monthName = localizedText["april"];
  } else if (monthKey === "05") {
    monthName = localizedText["may"];
  } else if (monthKey === "06") {
    monthName = localizedText["june"];
  } else if (monthKey === "07") {
    monthName = localizedText["july"];
  } else if (monthKey === "08") {
    monthName = localizedText["august"];
  } else if (monthKey === "09") {
    monthName = localizedText["september"];
  } else if (monthKey === "10") {
    monthName = localizedText["october"];
  } else if (monthKey === "11") {
    monthName = localizedText["november"];
  } else if (monthKey === "12") {
    monthName = localizedText["december"];
  }

  return monthName;
}

export function getFlagCountryCode(language, domicile) {
  //console.log("getFlagCountryCode", language, domicile);
  const lang = language.toLowerCase();
  const dom = domicile.toUpperCase();

  // Default flags by language
  const languageDefaults = {
    en: "US",
    es: "ES",
    pt: "BR",
    tr: "TR",
    fr: "FR",
    kr: "KR", // ✅ Korean → South Korea
    ja: "JP", // Japanese → Japan
    zh: "CN", // Chinese → China
    de: "DE", // German → Germany
    ru: "RU", // Russian → Russia
  };

  const spanishCountries = [
    "CO",
    "PE",
    "MX",
    "AR",
    "CL",
    "VE",
    "UY",
    "EC",
    "BO",
    "PY",
    "GT",
    "HN",
    "SV",
    "NI",
    "CR",
    "PA",
    "DO",
  ];

  if (lang === "es" && spanishCountries.includes(dom)) return dom;
  if (lang === "pt" && ["PT", "BR"].includes(dom)) return dom;
  if (lang === "en" && ["US", "GB", "AU", "CA"].includes(dom)) return dom;

  // Return default flag for language if available
  if (languageDefaults[lang]) return languageDefaults[lang];

  // Fallback to region/domicile
  return dom || "US";
}
